import { FieldLabel, Flex, NumericFormatField } from '@/components';
import type { ProjectSearchFormType } from '@/features/search/_models/projects-search';
import {
  type AmountSearchConditionFieldName,
  isTrendFieldDisabled,
} from '@/features/search/_utils/project-amount-search';
import { type FC, memo, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = {
  name:
    | AmountSearchConditionFieldName<'baseAmountValueFrom'>
    | AmountSearchConditionFieldName<'baseAmountValueTo'>
    | AmountSearchConditionFieldName<'trendConditionValue'>;
  yearFieldName:
    | AmountSearchConditionFieldName<'baseAmountYear'>
    | AmountSearchConditionFieldName<'targetAmountYear'>;
  label: string;
  index: number;
} & (
  | {
      conditionType: 'threshold';
      baseAmountTypeFieldName: AmountSearchConditionFieldName<'baseAmountType'>;
      targetAmountYearFieldName?: never;
      yearFieldName:
        | AmountSearchConditionFieldName<'baseAmountYear'>
        | AmountSearchConditionFieldName<'targetAmountYear'>;
    }
  | {
      conditionType: 'trend';
      yearFieldName: AmountSearchConditionFieldName<'baseAmountYear'>;
      targetAmountYearFieldName: AmountSearchConditionFieldName<'targetAmountYear'>;
      baseAmountTypeFieldName?: never;
    }
);

export const AmountSearchValueField: FC<Props> = memo(
  ({
    name,
    yearFieldName,
    label,
    index,
    conditionType,
    baseAmountTypeFieldName,
    targetAmountYearFieldName,
  }) => {
    const { t } = useTranslation();
    const {
      control,
      formState: { errors },
    } = useFormContext<ProjectSearchFormType>();
    const yearValue = useWatch({ control, name: yearFieldName });
    const targetAmountYearValue = targetAmountYearFieldName
      ? useWatch({ control, name: targetAmountYearFieldName })
      : undefined;
    const baseAmountValueIsnull = useWatch({
      control,
      name: `formulaConditions.${index}.baseAmountValueIsnull`,
    });
    const trendCondition = useWatch({
      control,
      name: `formulaConditions.${index}.trendCondition`,
    });
    const baseAmountType = baseAmountTypeFieldName
      ? useWatch({
          control,
          name: baseAmountTypeFieldName,
        })
      : undefined;

    const disabled = useMemo(() => {
      if (conditionType === 'threshold') {
        return !yearValue || !!baseAmountValueIsnull;
      }
      return (
        isTrendFieldDisabled(yearValue, targetAmountYearValue) ||
        trendCondition === 'noChange'
      );
    }, [
      conditionType,
      yearValue,
      baseAmountValueIsnull,
      targetAmountYearValue,
      trendCondition,
    ]);

    return (
      <Flex gap="4px">
        <NumericFormatField
          // やりたくないが高さを揃えるため
          label={label}
          width={150}
          name={name}
          control={control}
          errors={errors}
          disabled={disabled}
          allowNegative={conditionType === 'threshold'}
        />
        {conditionType === 'threshold' && (
          <FieldLabel
            style={{ marginTop: '34px' }}
            text={
              baseAmountType === 'executionRate'
                ? t('percentage-unit')
                : t('thousand-yen-unit')
            }
          />
        )}
      </Flex>
    );
  },
);
